import { List, Paper, makeStyles } from "@material-ui/core";
import { AccountCircle, Error } from "@material-ui/icons";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ItemsState } from "../../redux/state";
import { ListItem } from "@ofqual/portal-components/components/List/ListItem";
import { useLookup } from "@ofqual/portal-core/core/hooks/useLookup";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { foiService, foiSourceService } from "../../services";
import { FoiStage, FoiStatus } from "../../domain/FoiItem";
import { Opts } from "@ofqual/portal-core/core/types";
import { SlaDayConfig } from "../../domain/SlaDayConfig";

const SEARCH_STATE_KEY = "search";

const useStyles = makeStyles((theme) => ({
  active: {
    ...theme.list.itemPrimary,
  },
}));

type StageCalculationObject = {
  overdue: boolean;
  text: string;
  icon?: any | undefined;
  color?: string | undefined;
};

export const ItemsList: React.FC<{ state: ItemsState }> = ({ state }) => {
  const classes = useStyles();

  const { response } = state;
  const history = useHistory();
  const { pathname, search } = useLocation();
  const navigate = (id: string) =>
    history.push(`${pathname}/${id}`, { [SEARCH_STATE_KEY]: search });

  const [sourceLookups, foiLookups] = useLookup(foiSourceService, {
    service: {
      getAll: (opts: Opts) => foiService.getSlaConfig(opts),
    },
  });

  const slaStageConfig: SlaDayConfig = (foiLookups[0] as unknown) as SlaDayConfig;

  return (
    <Paper>
      <List disablePadding>
        {response.data.map((item) => {
          let stageCalc: StageCalculationObject = {
            overdue: false,
            text: `Stage: ${FoiStage[item.stage ?? -1] || "Unknown"}`,
          };

          const setStageChipDetails = (
            daysActive :number,
            slaDays: number,
            itemReceived? : any,
            icon?: any,
            color?: string
          ) => {

            var today: Date = new Date();
            if (itemReceived !== null) {
              var received: Date = new Date(itemReceived!);
              if (today.getDate() > received.getDate()) {
                daysActive += 1;
              }
            }

            if (
              item.status !== FoiStatus.Cancelled &&
              item.status !== FoiStatus.Closed
            ) {
              if (daysActive < slaDays) {
                stageCalc = {
                  overdue: false,
                  text: `Stage: ${FoiStage[item.stage ?? -1] || `Unknown`} (${
                    slaDays - daysActive === 1
                      ? `${slaDays - daysActive} day remaining`
                      : `${slaDays - daysActive} days remaining`
                  })`,
                };
              } else {
                stageCalc = {
                  overdue: true,
                  text: `Stage: ${FoiStage[item.stage ?? -1] || `Unknown`} (${
                    daysActive - slaDays === 0
                      ? `Overdue)`
                      : daysActive - slaDays === 1
                      ?  `${daysActive - slaDays}day overdue)`
                      : `${daysActive - slaDays} days overdue)`
                  }`,
                  icon: icon,
                  color: color,
                };
              }
            } else {
              stageCalc = {
                overdue: false,
                text: `Stage: ${FoiStage[item.stage ?? -1] || `Unknown`}`,
              };
            }
          };

          if (slaStageConfig !== undefined) {
            switch (item.stage) {
              case FoiStage["Stage 1"]:
                setStageChipDetails(
                  item.unpausedTime,
                  slaStageConfig.stageOne,
                  item.dateReceived,
                  <Error />,
                  "#cd5c5c"
                );
                break;
              case FoiStage["Stage 2"]:
                setStageChipDetails(
                  item.unpausedTime,
                  slaStageConfig.stageTwo,
                  item.dateReceived,
                  <Error />,
                  "#cd5c5c"
                );
                break;
              case FoiStage["Stage 3"]:
                setStageChipDetails(
                  item.unpausedTime,
                  slaStageConfig.stageThree,
                  item.dateReceived,
                  <Error />,
                  "#cd5c5c"
                );
                break;
              case FoiStage["Internal Review"]:
                setStageChipDetails(
                  item.internalReviewTime,
                  slaStageConfig.internalReview,
                  null,
                  <Error />,
                  "#cd5c5c"
                );
                break;
              case FoiStage["ICO"]:
                setStageChipDetails(
                  item.icoTime,
                  slaStageConfig.ico,
                  item.dateReceived,
                  <Error />,
                  "#cd5c5c"
                );
                break;
              default:
                break;
            }
          }

          return (
            <React.Fragment key={item.id}>
              <ListItem
                Icon={AccountCircle}
                button
                alignItems="flex-start"
                onClick={() => navigate(item.id)}
                divider
                className={classes.active}
                data-test="row"
                primaryText={`${item.caseTitle}`}
                reference={item.referenceNumber}
                secondaryDetails={[
                  {
                    heading: "Case lead",
                    value: item.caseLeadDisplayName ?? "Unassigned",
                  },
                  {
                    heading: "Source",
                    value:
                      sourceLookups.find((x) => x.id === item.sourceId)
                        ?.label ?? "Unknown",
                  },
                  {
                    heading: "Received on",
                    value: !!item.dateReceived
                      ? formatUtcDate(
                          item.dateReceived,
                          standardDateFormats.DATE
                        )
                      : "Unknown",
                  },
                ]}
                chipTexts={[
                  {
                    text: `Status: ${FoiStatus[item.status] ?? "Unknown"}`,
                  },
                  {
                    text: stageCalc.text,
                    icon: stageCalc.icon,
                    colour: stageCalc.color,
                  },
                ]}
              />
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};
